<div class="wizard-container">
  <!-- Wizard Header with Step Indicator -->
  <div class="wizard-header">
    <h1 class="wizard-title">Create Request</h1>
    <div class="step-indicator">
      @for (step of steps; track step.stepNumber; let i = $index) {
        <div
          class="step"
          [class.completed]="isStepCompleted(step.stepNumber)"
          [class.current]="isStepCurrent(step.stepNumber)">
          @if (i > 0) {
            <div class="step-connector"></div>
          }
          <div class="step-circle">
            @if (!isStepCompleted(step.stepNumber)) {
              <span>{{ step.stepNumber }}</span>
            }
            @if (isStepCompleted(step.stepNumber)) {
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            }
          </div>
          <span class="step-label">{{ step.label }}</span>
        </div>
      }
    </div>
  </div>

  <!-- Wizard Content -->
  <div class="wizard-content">
    <!-- Step 1: Contact Info -->
    @if (currentStep === 1) {
      <div class="step-content">
        <h2 class="step-title">Contact Information</h2>
        <p class="step-description">
          Please verify your contact information below. All fields are required.
        </p>

        <form [formGroup]="step1Form" class="wizard-form">
          <div class="form-row">
            <div class="form-group">
              <label for="companyName"
                >Company <span class="required">*</span></label
              >
              <div class="company-search-wrapper">
                <input
                  id="companyName"
                  type="text"
                  formControlName="companyName"
                  class="form-control"
                  placeholder="Search by company name or AR number"
                  autocomplete="off"
                  [class.invalid]="
                    step1Form.get('companyName')?.invalid &&
                    step1Form.get('companyName')?.touched
                  " />
                @if (isLookingUpCompany) {
                  <div class="info-message">Searching...</div>
                }
                @if (companySearchResults.length > 0) {
                  <div class="company-dropdown">
                    @for (
                      company of companySearchResults;
                      track company.arNumber
                    ) {
                      <button
                        type="button"
                        class="dropdown-item"
                        (click)="selectCompany(company)">
                        <div class="company-name">
                          {{ company.companyName }}
                        </div>
                        <div class="company-ar">{{ company.arNumber }}</div>
                      </button>
                    }
                  </div>
                }
                @if (
                  companyLookupError &&
                  step1Form.get('companyName')?.value &&
                  companySearchResults.length === 0
                ) {
                  <div class="error-message">{{ companyLookupError }}</div>
                }
              </div>
              @if (
                step1Form.get('companyName')?.invalid &&
                step1Form.get('companyName')?.touched
              ) {
                <div class="error-message">Company is required</div>
              }
            </div>

            <div class="form-group">
              <label for="arNumber"
                >AR Number <span class="required">*</span></label
              >
              <input
                id="arNumber"
                type="text"
                formControlName="arNumber"
                class="form-control"
                readonly
                placeholder="Auto-populated from Company" />
              @if (arNumberMissing) {
                <div class="error-message">
                  Please select a company to auto-populate the AR Number
                </div>
              }
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="firstName"
                >First Name <span class="required">*</span></label
              >
              <input
                id="firstName"
                type="text"
                formControlName="firstName"
                class="form-control"
                placeholder="First Name"
                [class.invalid]="
                  step1Form.get('firstName')?.invalid &&
                  step1Form.get('firstName')?.touched
                " />
              @if (
                step1Form.get('firstName')?.invalid &&
                step1Form.get('firstName')?.touched
              ) {
                <div class="error-message">First Name is required</div>
              }
            </div>

            <div class="form-group">
              <label for="lastName"
                >Last Name <span class="required">*</span></label
              >
              <input
                id="lastName"
                type="text"
                formControlName="lastName"
                class="form-control"
                placeholder="Last Name"
                [class.invalid]="
                  step1Form.get('lastName')?.invalid &&
                  step1Form.get('lastName')?.touched
                " />
              @if (
                step1Form.get('lastName')?.invalid &&
                step1Form.get('lastName')?.touched
              ) {
                <div class="error-message">Last Name is required</div>
              }
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="phoneNumber"
                >Phone Number <span class="required">*</span></label
              >
              <input
                id="phoneNumber"
                type="text"
                formControlName="phoneNumber"
                class="form-control"
                placeholder="Phone Number"
                [class.invalid]="
                  step1Form.get('phoneNumber')?.invalid &&
                  step1Form.get('phoneNumber')?.touched
                " />
              @if (
                step1Form.get('phoneNumber')?.invalid &&
                step1Form.get('phoneNumber')?.touched
              ) {
                <div class="error-message">Valid phone number is required</div>
              }
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="email"
                >Email Address(es) <span class="required">*</span></label
              >
              <textarea
                id="email"
                formControlName="email"
                class="form-control"
                placeholder="Enter email address(es)"
                rows="3"
                [class.invalid]="
                  step1Form.get('email')?.invalid &&
                  step1Form.get('email')?.touched
                "></textarea>
              <small class="field-hint"
                >You can enter multiple email addresses</small
              >
              @if (
                step1Form.get('email')?.invalid &&
                step1Form.get('email')?.touched
              ) {
                <div class="error-message">Valid email address is required</div>
              }
            </div>
          </div>
        </form>
      </div>
    }

    <!-- Step 2: Upload Files -->
    @if (currentStep === 2) {
      <div class="step-content step-upload">
        <app-step-upload-files
          (filesChanged)="setFilesUploaded($event)"></app-step-upload-files>
      </div>
    }

    <!-- Step 3: Expedite Request -->
    @if (currentStep === 3) {
      <app-step-expedite-request></app-step-expedite-request>
    }

    <!-- Step 4: Summary -->
    @if (currentStep === 4) {
      <app-step-summary
        [contactData]="contactData"
        [parsedFiles]="parsedFiles"
        [fileTotals]="fileTotals"
        [isExpedited]="isExpedited"></app-step-summary>
      @if (submitError) {
        <div class="submit-error-banner" role="alert">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ submitError }}</span>
        </div>
      }
    }

    <!-- Step 5: Confirmation -->
    @if (currentStep === 5) {
      <app-step-confirmation
        [requestNumber]="submittedRequestNumber!"
        (viewRequest)="onViewRequest()">
      </app-step-confirmation>
    }
  </div>

  <!-- Wizard Footer with Navigation -->
  <!-- Standard footer for Steps 1-4 -->
  @if (currentStep < 5) {
    <div class="wizard-footer">
      <ukg-button
        emphasis="mid"
        size="medium"
        type="button"
        (click)="onCancel()"
        (keydown.enter)="onCancel()"
        (keydown.space)="onCancel(); $event.preventDefault()">
        Cancel
      </ukg-button>
      <div class="nav-buttons">
        <ukg-button
          emphasis="high"
          size="medium"
          type="button"
          [disabled]="isFirstStep"
          (click)="onBack()"
          (keydown.enter)="onBack()"
          (keydown.space)="onBack(); $event.preventDefault()">
          Back
        </ukg-button>
        <ukg-button
          emphasis="high"
          size="medium"
          type="button"
          [disabled]="isSubmitting && currentStep === 4"
          (click)="onNext()"
          (keydown.enter)="onNext()"
          (keydown.space)="onNext(); $event.preventDefault()">
          {{
            isSubmitting && currentStep === 4
              ? 'Saving...'
              : currentStep === 4
                ? 'Save'
                : 'Next'
          }}
        </ukg-button>
      </div>
    </div>
  }

  <!-- Confirmation footer for Step 5 -->
  @if (currentStep === 5) {
    <div class="wizard-footer wizard-footer--confirmation">
      <ukg-button
        emphasis="high"
        size="medium"
        type="button"
        data-testid="btn-back-to-requests"
        (click)="onBackToRequests()"
        (keydown.enter)="onBackToRequests()"
        (keydown.space)="onBackToRequests(); $event.preventDefault()">
        Back to Requests
      </ukg-button>
    </div>
  }
</div>
